import React, { useCallback, useMemo, useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const DEFAULT_LINES = [
  'E3D scores are decomposed into named OTA terms instead of hidden weights.',
  'Open the methodology drawer or /methodology to see the live equation and thresholds.',
];

export default function ScoreMethodologyTooltip({
  title = 'OTA Methodology',
  lines = DEFAULT_LINES,
  equation = '',
  placement = 'top',
  glyph = 'info',
  onActivate = null,
  buttonStyle = null,
  ariaLabel = 'Score methodology',
}) {
  const [open, setOpen] = useState(false);

  const isTouchDevice = useMemo(() => {
    try {
      if (typeof window === 'undefined') return false;
      return (
        (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) ||
        (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
      );
    } catch (e) {
      return false;
    }
  }, []);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((value) => !value), []);

  const handleClick = useCallback((e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (typeof onActivate === 'function') {
      onActivate(e);
      return;
    }
    toggle();
  }, [onActivate, toggle]);

  const handleMouseEnter = useCallback(() => {
    if (isTouchDevice || typeof onActivate === 'function') return;
    setOpen(true);
  }, [isTouchDevice, onActivate]);

  const handleMouseLeave = useCallback(() => {
    if (isTouchDevice || typeof onActivate === 'function') return;
    setOpen(false);
  }, [isTouchDevice, onActivate]);

  const content = (
    <div style={{ maxWidth: 280 }}>
      <div style={{ fontWeight: 800, marginBottom: lines.length || equation ? 6 : 0 }}>{title}</div>
      {equation ? (
        <div style={{ fontFamily: '"SFMono-Regular", Consolas, monospace', color: '#9ec4ff', marginBottom: lines.length ? 6 : 0 }}>
          {equation}
        </div>
      ) : null}
      {lines.map((line, index) => (
        <div key={`${line}-${index}`} style={{ whiteSpace: 'pre-line' }}>{line}</div>
      ))}
    </div>
  );

  const sharedProps = {
    size: 'small',
    onClick: handleClick,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: typeof onActivate === 'function' ? undefined : () => setOpen(true),
    onBlur: typeof onActivate === 'function' ? undefined : () => setOpen(false),
    style: buttonStyle || { padding: 2, marginLeft: 4, color: '#9ea4d8' },
    'aria-label': ariaLabel,
  };

  const trigger = glyph === 'sqrt' ? (
    <IconButton {...sharedProps}>
      <span style={{ fontSize: 14, fontWeight: 900, lineHeight: 1, fontFamily: '"SFMono-Regular", Consolas, monospace' }}>
        √
      </span>
    </IconButton>
  ) : (
    <IconButton {...sharedProps}>
      <InfoOutlinedIcon style={{ fontSize: 14 }} />
    </IconButton>
  );

  if (typeof onActivate === 'function') {
    return trigger;
  }

  return (
    <Tooltip
      open={open}
      onClose={close}
      title={content}
      placement={placement}
      arrow
      interactive
    >
      {trigger}
    </Tooltip>
  );
}
